import React, { useState, useEffect, useContext } from "react";
import {
  PlusCircle,
  ArrowUpCircle,
  ArrowDownCircle,
  XCircle,
  User,
  Truck,
  FileText,
  Calendar,
  Paperclip,
  Edit3,
  Trash2,
  Eye,
  Search,
  Filter,
  Download,
} from "lucide-react";
import api from "../api/axiosInstance";
import { AuthContext } from "../App";

const CashLedger = () => {
  const { user } = useContext(AuthContext);
  const [transactions, setTransactions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals & View State
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  // Filter State
  const [filters, setFilters] = useState({ entity: "", project: "", type: "" });

  const initialForm = {
    id: "",
    project_id: "",
    type: "Income",
    category: "Client Payment",
    amount: "",
    entity_id: "",
    entity_name: "",
    payment_mode: "Cash",
    transaction_date: new Date().toISOString().split("T")[0],
    description: "",
    document: null,
  };
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [transRes, projRes, clientRes, vendorRes] = await Promise.all([
        api.get("get_transactions.php"),
        api.get("get_projects.php"),
        api.get("get_directory.php?type=client"),
        api.get("get_directory.php?type=vendor"),
      ]);
      setTransactions(transRes.data.transactions || []);
      setProjects(projRes.data.projects || []);
      setClients(clientRes.data.data || []);
      setVendors(vendorRes.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter((t) => {
    return (
      (filters.entity === "" || t.entity_id == filters.entity) &&
      (filters.project === "" || t.project_id == filters.project) &&
      (filters.type === "" || t.type === filters.type)
    );
  });

  const handleEdit = (t) => {
    setIsEdit(true);
    setFormData({ ...t, document: null }); // Don't reload file object
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "document" && formData[key]) {
        data.append("document", formData[key]);
      } else {
        data.append(key, formData[key]);
      }
    });
    data.append("action", isEdit ? "update" : "create");

    try {
      await api.post("manage_transactions.php", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setShowModal(false);
      setFormData(initialForm);
      loadData();
    } catch (err) {
      alert("Error saving transaction");
    }
  };

  const softDelete = async (id) => {
    if (!window.confirm("Move this transaction to trash?")) return;
    try {
      await api.post("manage_transactions.php", { id, action: "soft_delete" });
      loadData();
    } catch (err) {
      alert("Delete failed");
    }
  };
  return (
    <div className="space-y-6 p-4 animate-in fade-in duration-500">
      {/* Header & Main Actions */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase italic tracking-tighter leading-none">
            Finance Ledger
          </h1>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
            {`${user.company_name} Audit Mode`}
          </p>
        </div>
        <button
          onClick={() => {
            setIsEdit(false);
            setFormData(initialForm);
            setShowModal(true);
          }}
          className="bg-indigo-600 text-white px-8 py-4 rounded-3xl font-black text-[10px] uppercase shadow-xl hover:bg-slate-900 transition-all flex items-center gap-2"
        >
          <PlusCircle size={18} /> Record Entry
        </button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-2xl border border-slate-100 min-w-[200px]">
          <Filter size={14} className="text-slate-400" />
          <select
            className="bg-transparent text-[10px] font-black uppercase outline-none w-full"
            value={filters.entity}
            onChange={(e) => setFilters({ ...filters, entity: e.target.value })}
          >
            <option value="">All Clients / Vendors</option>
            <optgroup label="Clients">
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </optgroup>
            <optgroup label="Vendors">
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.vendor_name}
                </option>
              ))}
            </optgroup>
          </select>
        </div>

        <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-2xl border border-slate-100 min-w-[180px]">
          <Search size={14} className="text-slate-400" />
          <select
            className="bg-transparent text-[10px] font-black uppercase outline-none w-full"
            value={filters.project}
            onChange={(e) =>
              setFilters({ ...filters, project: e.target.value })
            }
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.project_name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex bg-slate-100 p-1 rounded-2xl">
          {["", "Income", "Expense"].map((type) => (
            <button
              key={type}
              onClick={() => setFilters({ ...filters, type })}
              className={`px-4 py-1.5 rounded-xl text-[9px] font-black uppercase transition-all ${filters.type === type ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400"}`}
            >
              {type || "All"}
            </button>
          ))}
        </div>
      </div>

      {/* Ledger Table */}
      <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest">
            <tr>
              <th className="p-6">Trans. Date</th>
              <th className="p-6">Party / Site</th>
              <th className="p-6">Amount</th>
              <th className="p-6 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filteredTransactions.map((t) => (
              <tr
                key={t.id}
                className="hover:bg-slate-50/50 transition-all group"
              >
                <td className="p-6">
                  <p className="font-black text-slate-900 text-xs italic">
                    {t.transaction_date}
                  </p>
                  <p
                    className={`text-[8px] font-black uppercase ${t.type === "Income" ? "text-emerald-500" : "text-rose-500"}`}
                  >
                    {t.type}
                  </p>
                </td>
                <td className="p-6">
                  <p className="font-black text-slate-900 text-xs uppercase tracking-tight">
                    {t.entity_name}
                  </p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase">
                    {t.project_name || "General"}
                  </p>
                </td>
                <td
                  className={`p-6 font-black text-sm italic ${t.type === "Income" ? "text-emerald-600" : "text-rose-600"}`}
                >
                  {t.type === "Income" ? "+" : "-"} ₹
                  {parseFloat(t.amount).toLocaleString()}
                </td>
                <td className="p-6 text-right space-x-1">
                  <button
                    onClick={() => {
                      setSelectedEntry(t);
                      setShowViewModal(true);
                    }}
                    className="p-2 text-slate-400 hover:text-indigo-600 bg-slate-50 rounded-lg"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setIsEdit(true);
                      setFormData({ ...t, document: null });
                      setShowModal(true);
                    }}
                    className="p-2 text-slate-400 hover:text-amber-600 bg-slate-50 rounded-lg"
                  >
                    <Edit3 size={16} />
                  </button>
                  <button
                    onClick={() => {
                      softDelete(t.id);
                    }}
                    className="p-2 text-slate-400 hover:text-rose-600 bg-slate-50 rounded-lg"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* VIEW MODAL (Entry & Attachment View) */}
      {showViewModal && selectedEntry && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-[48px] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div>
                <h2 className="text-2xl font-black italic uppercase tracking-tighter">
                  Transaction Detail
                </h2>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em]">
                  ID: TXN-{selectedEntry.id.toString().padStart(5, "0")}
                </p>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="p-3 bg-white text-slate-400 hover:text-rose-500 rounded-2xl shadow-sm transition-all"
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Information side */}
              <div className="space-y-8">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase mb-1">
                      Party Name
                    </p>
                    <p className="text-lg font-black text-slate-900 uppercase italic leading-none">
                      {selectedEntry.entity_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase mb-1">
                      Date
                    </p>
                    <p className="text-lg font-black text-slate-900 uppercase italic leading-none">
                      {selectedEntry.transaction_date}
                    </p>
                  </div>
                </div>

                <div className="p-6 rounded-[32px] bg-slate-900 text-white relative overflow-hidden">
                  <p className="text-[10px] font-black uppercase text-indigo-300 mb-2">
                    Transaction Value
                  </p>
                  <h3 className="text-4xl font-black italic">
                    ₹{parseFloat(selectedEntry.amount).toLocaleString()}
                  </h3>
                  <p className="text-[10px] font-bold mt-2 uppercase opacity-60">
                    Via {selectedEntry.payment_mode}
                  </p>
                  <div
                    className={`absolute top-4 right-4 px-3 py-1 rounded-full text-[8px] font-black uppercase ${selectedEntry.type === "Income" ? "bg-emerald-500" : "bg-rose-500"}`}
                  >
                    {selectedEntry.type}
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase mb-2">
                    Description / Notes
                  </p>
                  <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 italic text-sm text-slate-600 font-bold leading-relaxed">
                    {selectedEntry.description ||
                      "No specific internal notes attached to this transaction."}
                  </div>
                </div>
              </div>

              {/* Attachment side */}
              <div className="flex flex-col h-full">
                <p className="text-[10px] font-black text-slate-400 uppercase mb-4 flex items-center gap-2">
                  <Paperclip size={14} className="text-indigo-500" /> Verified
                  Attachment
                </p>
                {selectedEntry.document_path ? (
                  <div className="flex-1 bg-slate-100 rounded-[32px] overflow-hidden border-4 border-white shadow-inner relative group">
                    <img
                      src={`${api.defaults.baseURL}/uploads/${selectedEntry.document_path}`}
                      className="w-full h-full object-contain"
                      alt="Attachment"
                    />
                    <a
                      href={`${api.defaults.baseURL}/uploads/${selectedEntry.document_path}`}
                      target="_blank"
                      className="absolute bottom-4 right-4 bg-white/90 backdrop-blur p-4 rounded-2xl shadow-xl flex items-center gap-2 text-[10px] font-black uppercase hover:bg-slate-900 hover:text-white transition-all"
                    >
                      <Download size={14} /> Download Proof
                    </a>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 border-4 border-dashed border-slate-100 rounded-[32px] text-slate-300">
                    <FileText size={48} className="opacity-10 mb-2" />
                    <p className="text-[9px] font-black uppercase tracking-widest">
                      No Document Attached
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form
            onSubmit={handleSave}
            className="bg-white rounded-[48px] p-10 w-full max-w-2xl shadow-2xl animate-in zoom-in-95 overflow-y-auto max-h-[90vh]"
          >
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-black italic uppercase tracking-tighter">
                {isEdit ? "Modify Record" : "New Transaction"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:bg-slate-100 rounded-full p-2"
              >
                <XCircle />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2 flex bg-slate-100 p-2 rounded-3xl gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, type: "Income" })}
                  className={`flex-1 py-4 rounded-2xl font-black text-[10px] uppercase transition-all ${formData.type === "Income" ? "bg-emerald-500 text-white shadow-lg" : "text-slate-400"}`}
                >
                  Payment In (Receipt)
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, type: "Expense" })}
                  className={`flex-1 py-4 rounded-2xl font-black text-[10px] uppercase transition-all ${formData.type === "Expense" ? "bg-rose-500 text-white shadow-lg" : "text-slate-400"}`}
                >
                  Payment Out (Voucher)
                </button>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 ml-4 mb-1 block uppercase">
                  Entity
                </label>
                <select
                  required
                  className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-sm outline-none"
                  value={formData.entity_id}
                  onChange={(e) => {
                    const list = formData.type === "Income" ? clients : vendors;
                    const sel = list.find((x) => x.id == e.target.value);
                    setFormData({
                      ...formData,
                      entity_id: e.target.value,
                      entity_name: sel ? sel.name || sel.vendor_name : "",
                    });
                  }}
                >
                  <option value="">Select...</option>
                  {formData.type === "Income"
                    ? clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))
                    : vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.vendor_name}
                        </option>
                      ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 ml-4 mb-1 block uppercase">
                  Project Site
                </label>
                <select
                  className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-sm outline-none"
                  value={formData.project_id}
                  onChange={(e) =>
                    setFormData({ ...formData, project_id: e.target.value })
                  }
                >
                  <option value="">General / Office</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.project_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 ml-4 mb-1 block uppercase">
                  Amount
                </label>
                <input
                  required
                  type="number"
                  className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-sm outline-none"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData({ ...formData, amount: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 ml-4 mb-1 block uppercase">
                  Transaction Date
                </label>
                <input
                  type="date"
                  className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-sm outline-none"
                  value={formData.transaction_date}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      transaction_date: e.target.value,
                    })
                  }
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-[10px] font-black text-slate-400 ml-4 mb-1 block uppercase">
                  Description / Internal Notes
                </label>
                <textarea
                  rows="3"
                  className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-sm outline-none resize-none"
                  placeholder="e.g. Being payment for plywood order #123"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-[10px] font-black text-slate-400 ml-4 mb-1 block uppercase">
                  Attach Proof (Bill/Receipt Image)
                </label>
                <input
                  type="file"
                  className="w-full p-4 bg-slate-50 border-dashed border-2 border-slate-200 rounded-2xl font-bold text-xs"
                  onChange={(e) =>
                    setFormData({ ...formData, document: e.target.files[0] })
                  }
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full mt-10 bg-slate-900 text-white p-5 rounded-3xl font-black uppercase italic tracking-widest hover:bg-indigo-600 transition-all shadow-xl"
            >
              Confirm Transaction
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default CashLedger;
